import { type FilterBaseInterface } from "@finway-group/shared/lib/models/filter/base.filter"
import { useMutation, useQuery } from "@tanstack/react-query"
import { queryClient } from "~Shared/config/reactQueryConfig"
import { FilterService } from "~Shared/services/filter.service"
import { TablesEnum } from "~Shared/store/reducers/tableConfigReducer"
import { CacheEnum, GENERAL_QUERY_STALE_TIME } from "./consts"

/** Tables whose filters are loaded via React Query; socket sync skips Redux for these. */
export const REACT_QUERY_FILTER_TABLES = [TablesEnum.OPOS_LIST]

export type FilterTableKey = (typeof REACT_QUERY_FILTER_TABLES)[number]

const FILTER_QUERY_KEY = [CacheEnum.FILTERS]

export const invalidateFilters = () => queryClient.invalidateQueries({ queryKey: FILTER_QUERY_KEY })

export const useFiltersQuery = () =>
    useQuery({
        queryKey: FILTER_QUERY_KEY,
        queryFn: ({ signal }) => FilterService.getFilters({ signal }),
        staleTime: GENERAL_QUERY_STALE_TIME,
    })

const EMPTY_FILTERS: Record<string, unknown> = {}

/** Returns saved filter parameters for a given table, or undefined if none. */
export const useFilterByTableQuery = <T extends FilterBaseInterface = FilterBaseInterface>(table: FilterTableKey) => {
    const { data: filters, ...rest } = useFiltersQuery()
    const parameters = filters?.find((f) => f.table === table)?.parameters as T | undefined
    return {
        ...rest,
        data: (parameters ?? EMPTY_FILTERS) as T,
        parameters,
    }
}

export const useSetFilterMutation = () =>
    useMutation({
        mutationFn: ({ table, parameters }: { table: FilterTableKey; parameters: FilterBaseInterface }) => FilterService.setFilter(table, parameters),
        onSuccess: () => {
            void invalidateFilters()
        },
    })
